import React, { ChangeEvent, useState } from 'react';
import {
  Button,
  Col,
  Divider,
  Margin,
  P,
  Row,
  Strong,
  TextField,
} from '@ovotech/element-v5';
import { formatPoundsAmount } from '@/src/utils/money';
import { strings } from './strings';
import { Card as NebulaCard } from '@ovotech/nebula'; // Temporarily using Nebula Card in some places to match other pages
import { BalanceBox, BalanceRow, ButtonsContainer } from './styles';

type Props = {
  currentBalanceInPence: number;
  maxTopupAmountInPence: number;
  minTopupAmountInPence: number;
  maxBalanceInPence: number;
  onBack: () => void;
  onContinue: (amountInPence: number) => Promise<void>;
};

export const PAYGTopupPageContent = ({
  currentBalanceInPence,
  maxTopupAmountInPence,
  minTopupAmountInPence,
  maxBalanceInPence,
  onBack,
  onContinue,
}: Props) => {
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const parsedAmount = Number(inputValue.replace(',', '.'));
  const amountInPence = isNaN(parsedAmount)
    ? 0
    : Math.round(parsedAmount * 100);
  const newBalanceInPence = currentBalanceInPence + amountInPence;

  const errorMessage = [
    {
      active: newBalanceInPence > maxBalanceInPence,
      message: strings.maxBalanceError(maxBalanceInPence),
    },
    {
      active: !Number.isInteger(parsedAmount),
      message: strings.nonIntegerError,
    },
    {
      active: amountInPence > maxTopupAmountInPence,
      message: strings.maxTopupError(maxTopupAmountInPence),
    },
    {
      active: inputValue !== '' && amountInPence < minTopupAmountInPence,
      message: strings.minTopupError(minTopupAmountInPence),
    },
  ].find(({ active }) => active)?.message;

  const handleAmountChange = ({
    target: { value },
  }: ChangeEvent<HTMLInputElement>) => {
    const validated = value.match(/^(?:\d{0,4}(?:[,.]\d{0,2})?)?$/);
    if (validated) {
      setInputValue(value);
    }
  };

  const isValid = inputValue !== '' && !errorMessage;

  return (
    <>
      <NebulaCard>
        <Row>
          <Col small={12} medium={6}>
            <TextField
              fullWidth
              id="payg-topup-amount"
              type="number"
              iconLeft="pound"
              label={strings.enterAmountLabel}
              value={inputValue}
              error={errorMessage}
              onChange={handleAmountChange}
              onBlur={() => {
                const parsed = parseInputValue(inputValue);
                if (inputValue === '' || isNaN(parsed)) {
                  setInputValue('');
                } else {
                  setInputValue(parsed.toString());
                }
              }}
            />

            <Margin bottom={4} />

            <BalanceBox>
              <BalanceRow>
                <P>{strings.currentBalance}</P>
                <P>{formatPoundsAmount(currentBalanceInPence / 100)}</P>
              </BalanceRow>
              <Divider />
              <BalanceRow>
                <P>
                  <Strong>{strings.newBalance}</Strong>
                </P>
                <P>
                  <Strong>{formatPoundsAmount(newBalanceInPence / 100)}</Strong>
                </P>
              </BalanceRow>
            </BalanceBox>
          </Col>
        </Row>
      </NebulaCard>

      <Margin bottom={6} />

      <ButtonsContainer>
        <Button variant="outline" iconLeft="chevron-left" onClick={onBack}>
          {strings.backButton}
        </Button>
        <Button
          disabled={!isValid || isLoading}
          onClick={async () => {
            setIsLoading(true);
            try {
              await onContinue(amountInPence);
            } finally {
              setIsLoading(false);
            }
          }}
        >
          {strings.continueButton}
        </Button>
      </ButtonsContainer>
    </>
  );
};

function parseInputValue(input: string) {
  return Number(input.replace(',', '.'));
}
